/**
 * layoutCodec — compact serialization for the furniture layout array.
 *
 * The scene state is stored uncompressed in localStorage, LZString-compressed into
 * the `?layout=` share URL, and pre-compressed in `public/data/layouts.json` presets.
 * The verbose per-item shape produced by `FurnitureManager.serializeLayout()` is
 * dominated by two random 36-char UUIDs (`id`/`attachedTo`) and full-precision floats,
 * both of which compress poorly. This codec rewrites that array to a compact form:
 *
 *   - UUID `id`/`attachedTo` → small sequential integer indices,
 *   - floats rounded (positions/offsets to mm, rotation to ~0.0001 rad),
 *   - short keys and omitted null/default fields.
 *
 * It is intentionally pure (no browser globals) so it runs under Vitest/Node and in
 * the one-off `scripts/recompress-layouts.mjs` regeneration tool.
 *
 * `version` '2'/'3' = compact ('3' in the +Z-front frame, see orientationMigration.js); legacy '1.0' passes through verbatim.
 */

/** Compact-format state version tag. */
export const STATE_VERSION = '3';
const COMPACT_VERSIONS = new Set(['2', '3']);

/**
 * The only keys a layout payload carries; dropping a pre-split payload's `environment` and view
 * toggles keeps them from fighting the user's own preferences (see src/core/UserPreferences.js).
 */
export const LAYOUT_FIELDS = ['version', 'furniture', 'collection', 'legTexture', 'legHeight', 'covers', 'camera'];

/** @returns {Object} `state` with only the layout fields kept. */
export function pickLayout(state) {
  const layout = {};
  for (const key of LAYOUT_FIELDS) {
    if (state[key] !== undefined) layout[key] = state[key];
  }
  return layout;
}

const POS_DECIMALS = 3; // mm precision for positions / offsets
const ROT_DECIMALS = 4; // ~0.0001 rad — visually irrelevant, preserves load-time π/epsilon handling

/** Round to `decimals` places; non-finite or nullish → 0. Normalizes -0 to 0. */
export function round(value, decimals) {
  if (!Number.isFinite(value)) return 0;
  const r = Number(value.toFixed(decimals));
  return r === 0 ? 0 : r;
}

/** Encode a {x,y,z} object as a rounded [x,y,z] tuple. */
function encodeVec(v, decimals) {
  return [round(v.x, decimals), round(v.y, decimals), round(v.z, decimals)];
}

/** Decode a [x,y,z] tuple back to a {x,y,z} object. */
function decodeVec(a) {
  return { x: a[0], y: a[1], z: a[2] };
}

/**
 * Convert the verbose furniture array (FurnitureManager.serializeLayout output) into
 * the compact representation. Root items omit `a` (attachedTo); null/default fields
 * are dropped entirely.
 * @param {Array<Object>} items
 * @returns {Array<Object>} compact items
 */
export function encodeFurniture(items) {
  if (!Array.isArray(items)) return [];

  // Map each item's UUID to its sequential index so attachedTo references can be rewritten.
  const idToIndex = new Map();
  items.forEach((item, index) => idToIndex.set(item.id, index));

  return items.map((item, index) => {
    const out = { i: index, u: item.urlCode };

    // Catalog item id, only when it differs from urlCode (same-urlCode variants like
    // diagon-bp-l). Most items have id === urlCode, so this is usually omitted.
    if (item.dataId && item.dataId !== item.urlCode) out.d = item.dataId;

    if (item.fabricId) out.f = item.fabricId;
    out.p = encodeVec(item.position, POS_DECIMALS);

    const rot = round(item.rotation, ROT_DECIMALS);
    if (rot !== 0) out.r = rot;

    if (item.attachedTo != null && idToIndex.has(item.attachedTo)) {
      out.a = idToIndex.get(item.attachedTo);
    }
    if (item.attachedSide) out.s = item.attachedSide;
    if (item.attachedPointOffset) out.o = encodeVec(item.attachedPointOffset, POS_DECIMALS);
    if (item.attachmentLocalPosition) out.l = encodeVec(item.attachmentLocalPosition, POS_DECIMALS);

    // componentType is read by both loadLayout and ThumbnailRenderer; always keep it.
    out.c = item.componentType;

    if (item.moduleConnectionSide) out.m = item.moduleConnectionSide;
    if (item.variantIndex) out.v = item.variantIndex;

    // Per-element wood finish. The omitted value is a *format* sentinel, not a live read of
    // DEFAULT_WOOD_TYPE: it decides how every v2 blob ever written is read back, so moving it
    // re-reads existing share links. It happens to match the current default; keep the literal.
    if (item.woodType && item.woodType !== 'dark') out.w = item.woodType;

    return out;
  });
}

/** True when `items` already look like the verbose (v1) shape. Empty counts as verbose — both codecs no-op on it. */
export function isVerbose(items) {
  return items.length === 0 || Object.prototype.hasOwnProperty.call(items[0], 'urlCode');
}

/**
 * Expand a furniture array back to the verbose shape consumed by
 * `FurnitureManager.loadLayout()` and `ThumbnailRenderer`. Legacy (v1) arrays — or any
 * array that is already verbose — pass through unchanged.
 * @param {Array<Object>} items
 * @param {string} [version] state.version; '2' and '3' mean compact
 * @returns {Array<Object>} verbose items
 */
export function decodeFurniture(items, version) {
  if (!Array.isArray(items)) return [];
  if (!COMPACT_VERSIONS.has(version) || isVerbose(items)) return items;

  return items.map((item) => ({
    id: String(item.i),
    urlCode: item.u,
    dataId: item.d ?? null,
    fabricId: item.f ?? null,
    position: decodeVec(item.p),
    rotation: item.r ?? 0,
    attachedTo: item.a != null ? String(item.a) : null,
    attachedSide: item.s ?? null,
    attachedPointOffset: item.o ? decodeVec(item.o) : null,
    attachmentLocalPosition: item.l ? decodeVec(item.l) : null,
    componentType: item.c,
    moduleConnectionSide: item.m ?? null,
    variantIndex: item.v ?? 0,
    woodType: item.w ?? null,
  }));
}
